/**
 * Homepage scroll animations.
 * - Hero copy rises in on load, line by line.
 * - Each section's heading, cards and panels fade up as they scroll into view, cards in a short stagger.
 * - The colour spheres pop up one after another with a little bounce.
 * Uses the CSS `translate` property so it never clashes with transforms used elsewhere (card flip, hover zoom).
 * Skipped entirely for shoppers who prefer reduced motion, and content stays visible if this script fails.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function init() {
  const main = document.getElementById('main-content');
  if (!main || reduceMotion || !('IntersectionObserver' in window)) return;

  // Hero: each line of text, then each button, wipes in left to right, top to bottom, like it's being typed.
  const heroCopy = main.querySelector('[data-hero-copy]');
  if (heroCopy) {
    const heroItems = [];
    [...heroCopy.children].forEach((el) => {
      const isButtonRow = el.querySelector('a, button') && !el.matches('a, button') && el.children.length > 1;
      if (isButtonRow) heroItems.push(...el.children);
      else heroItems.push(el);
    });
    let delay = 250;
    heroItems.forEach((el) => {
      const length = (el.textContent || '').trim().length;
      // Longer lines take a little longer to "type", within limits so nothing drags.
      const duration = Math.min(1400, Math.max(550, length * 28));
      el.classList.add('wp');
      el.style.setProperty('--wp-delay', `${delay}ms`);
      el.style.setProperty('--wp-duration', `${duration}ms`);
      delay += duration * 0.75;
    });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      heroItems.forEach((el) => el.classList.add('wp-in'));
    }));
  }

  // Sections below the hero.
  const sections = [...main.querySelectorAll('[aria-labelledby^="home-"]')].filter(
    (s) => s.getAttribute('aria-labelledby') !== 'home-hero-heading'
  );

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('rv-in');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

  sections.forEach((section) => {
    const picked = new Set();
    section.querySelectorAll('h2').forEach((h) => picked.add(h.parentElement));
    section.querySelectorAll('ul[role="list"] > li, details, s-newsletter-flip, [data-reveal]').forEach((el) => picked.add(el));
    if (section.getAttribute('aria-labelledby') === 'home-movement-heading') {
      [...section.children].forEach((el) => picked.add(el));
    }
    // Drop anything nested inside another picked element so nothing animates twice.
    const items = [...picked].filter((el) => el && el !== section && ![...picked].some((o) => o !== el && o.contains(el)));

    items.forEach((el) => {
      el.classList.add('rv');
      const siblings = el.parentElement ? [...el.parentElement.children].filter((c) => items.includes(c)) : [];
      const index = Math.max(0, siblings.indexOf(el));
      const pop = section.getAttribute('aria-labelledby') === 'home-finishes-heading' && el.matches('li');
      if (pop) el.classList.add('rv-pop');
      el.style.setProperty('--rv-delay', `${pop ? index * 130 : Math.min(index, 7) * 90}ms`);
      // Already on screen (e.g. after a refresh mid-page): show without waiting.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
        requestAnimationFrame(() => el.classList.add('rv-in'));
      } else {
        observer.observe(el);
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Once an element has finished revealing, drop the helper classes so its own transitions (hover, etc.) are untouched.
document.addEventListener('transitionend', (event) => {
  const el = event.target;
  if (!(el instanceof HTMLElement) || !el.classList.contains('rv-in') || event.propertyName !== 'opacity') return;
  el.classList.remove('rv', 'rv-in', 'rv-pop');
  el.style.removeProperty('--rv-delay');
});

// Same clean-up for the hero wipe, so buttons keep their normal hover and focus styles afterwards.
document.addEventListener('transitionend', (event) => {
  const el = event.target;
  if (!(el instanceof HTMLElement) || !el.classList.contains('wp-in') || event.propertyName !== 'clip-path') return;
  el.classList.remove('wp', 'wp-in');
  el.style.removeProperty('--wp-delay');
  el.style.removeProperty('--wp-duration');
});
